import React from 'react';
import { FaChevronLeft, FaChevronRight, FaAngleDoubleLeft, FaAngleDoubleRight, FaEllipsisH } from 'react-icons/fa';
import '../styles/taskManagement.css';

const Pagination = ({ itemsPerPage, totalItems, paginate, onPageChange, currentPage, totalPages: propsTotalPages }) => {
  // Ensure currentPage is a number for proper comparisons
  const currentPageNum = parseInt(currentPage, 10);
  // Handle both naming conventions (paginate and onPageChange)
  const handlePageChange = (pageNumber) => {
    if (paginate && typeof paginate === 'function') {
      paginate(pageNumber);
    } else if (onPageChange && typeof onPageChange === 'function') {
      onPageChange(pageNumber);
    }
  };
  
  // Use provided totalPages or calculate it
  const totalPages = propsTotalPages || (itemsPerPage ? Math.ceil(totalItems / itemsPerPage) : 1);
  
  // Don't render pagination if there's only one page
  if (totalPages <= 1) return null;
  
  // Generate page numbers array
  const getPageNumbers = () => {
    const pages = [];
    
    // Always show first page
    pages.push(1);
    
    // Calculate range around current page
    let startPage = Math.max(2, currentPageNum - 1);
    let endPage = Math.min(totalPages - 1, currentPageNum + 1);
    
    // Add ellipsis after first page if needed
    if (startPage > 2) {
      pages.push('ellipsis1');
    }
    
    // Add pages around current page
    for (let i = startPage; i <= endPage; i++) {
      if (i !== 1 && i !== totalPages) {
        pages.push(i);
      }
    }
    
    // Add ellipsis before last page if needed
    if (endPage < totalPages - 1) {
      pages.push('ellipsis2');
    }
    
    // Always show last page if more than 1 page
    if (totalPages > 1) {
      pages.push(totalPages);
    }
    
    return pages;
  };
  
  const pageNumbers = getPageNumbers();

  return (
    <div className="pagination-controls">
      {/* First Page Button */}
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (currentPageNum !== 1) handlePageChange(1);
        }}
        disabled={currentPageNum === 1}
        className={`pagination-button ${currentPageNum === 1 ? 'pagination-button-disabled' : 'pagination-button-inactive'}`}
        aria-label="First Page"
      >
        <FaAngleDoubleLeft />
      </button>

      {/* Previous Button */}
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (currentPageNum > 1) handlePageChange(currentPageNum - 1);
        }}
        disabled={currentPageNum === 1}
        className={`pagination-button ${currentPageNum === 1 ? 'pagination-button-disabled' : 'pagination-button-inactive'}`}
        aria-label="Previous Page"
      >
        <FaChevronLeft />
      </button>

      {/* Page Numbers */}
      {pageNumbers.map((pageNumber, index) => {
        if (pageNumber === 'ellipsis1' || pageNumber === 'ellipsis2') {
          return (
            <span key={pageNumber} className="pagination-ellipsis">
              <FaEllipsisH className="h-3 w-3" />
            </span>
          );
        }
        
        return (
          <button
            key={index}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handlePageChange(pageNumber);
            }}
            className={`pagination-button ${currentPageNum === pageNumber ? 'pagination-button-active' : 'pagination-button-inactive'}`}
          >
            {pageNumber}
          </button>
        );
      })}

      {/* Next Button */}
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (currentPageNum < totalPages) handlePageChange(currentPageNum + 1);
        }}
        disabled={currentPageNum === totalPages}
        className={`pagination-button ${currentPageNum === totalPages ? 'pagination-button-disabled' : 'pagination-button-inactive'}`}
        aria-label="Next Page"
      >
        <FaChevronRight />
      </button>

      {/* Last Page Button */}
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (currentPageNum !== totalPages) handlePageChange(totalPages);
        }}
        disabled={currentPageNum === totalPages}
        className={`pagination-button ${currentPageNum === totalPages ? 'pagination-button-disabled' : 'pagination-button-inactive'}`}
        aria-label="Last Page"
      >
        <FaAngleDoubleRight />
      </button>
    </div>
  );
};

export default Pagination;
